import { useState, type ReactNode } from 'react';
import { Button, Flash, Label } from '@primer/react';
import type { Proposal } from '@autonomous-team/domain';

import { formatTimestamp } from '../../logic/format.js';
import { useConsoleMutation } from '../queries.js';

export interface TeamChangeProposalCardProps {
  readonly proposal: Proposal;
  readonly pending: boolean;
  readonly error?: Error | null;
  readonly onApply: (note: string) => void;
  readonly onReject: (note: string) => void;
}

/** The exact, human-reviewable payload produced by Main Operator in Task Chat. */
export function TeamChangeProposalCard({ proposal, pending, error = null, onApply, onReject }: TeamChangeProposalCardProps): ReactNode {
  const [note, setNote] = useState('');
  const change = proposal.teamChange;
  if (proposal.kind !== 'team-change' || change === null) return null;
  const receipt = proposal.applicationReceipt;

  return (
    <article className="team-change-proposal" aria-label="Team change proposal">
      <header className="team-change-proposal-head">
        <div><Label variant="accent">Team change proposal</Label><span>{proposal.status}</span></div>
        <h3>{proposal.draft.title}</h3>
        <p>{proposal.draft.benefit}</p>
        <small>Proposed by {proposal.authorId} from Task Chat · revision {proposal.revision} · {formatTimestamp(proposal.createdAt)}</small>
      </header>

      <section>
        <h4>Why this should become team knowledge</h4>
        <p>{proposal.draft.rationale}</p>
        <p>{proposal.draft.body}</p>
      </section>

      <section>
        <h4>Team document</h4>
        <p>Base Team Version {change.baseTeamVersion}</p>
        <pre className="team-change-diff">{change.teamDocumentDiff.length > 0 ? change.teamDocumentDiff : 'No Team document changes.'}</pre>
        <details>
          <summary>Show exact candidate Team document</summary>
          <pre className="team-change-document">{change.candidateTeamDocument}</pre>
        </details>
      </section>

      {change.skillChanges.length === 0 ? null : (
        <section>
          <h4>Skills</h4>
          <div className="team-change-skills">
            {change.skillChanges.map((skill) => (
              <article key={skill.ref} className="team-change-skill">
                <strong>{skill.name}</strong><code>{skill.ref}</code>
                <p>Base: {skill.baseName ?? 'new skill'} · digest {skill.baseDigest ?? 'none'} · audience {skill.baseAudiences.length === 0 ? 'all agents' : skill.baseAudiences.join(', ')}</p>
                <p>Audience: {skill.audiences.length === 0 ? 'all agents' : skill.audiences.join(', ')}</p>
                <pre>{skill.text}</pre>
              </article>
            ))}
          </div>
        </section>
      )}

      <section>
        <h4>Current task</h4>
        <p>{change.currentTaskAction.kind === 'return-to-stage'
          ? <>Return this task to <strong>{change.currentTaskAction.stageId}</strong>: {change.currentTaskAction.reason}</>
          : <>No automatic workflow move: {change.currentTaskAction.reason}</>}</p>
      </section>

      {receipt === null ? null : (
        <section className={`team-change-receipt team-change-receipt-${receipt.deliveryState}`} aria-label="Application receipt">
          <h4>Application receipt</h4>
          <p><strong>Applied as Team Version {receipt.teamVersion}</strong> by {receipt.appliedBy} at {formatTimestamp(receipt.appliedAt)}.</p>
          <p>Team document digest: <code>{receipt.teamDocumentDigest}</code></p>
          {receipt.skills.length === 0 ? null : (
            <ul>{receipt.skills.map((skill) => <li key={skill.ref}><code>{skill.ref}</code> digest <code>{skill.digest}</code> · audience {skill.audiences.length === 0 ? 'all agents' : skill.audiences.join(', ')}</li>)}</ul>
          )}
          <p>{receipt.refreshedAgentHomes} agent homes refreshed · Delivery {receipt.deliveryState} · Current task action {receipt.currentTaskActionState}</p>
          {receipt.deliveryError === null ? null : <Flash variant="warning">Delivery still needs attention: {receipt.deliveryError}</Flash>}
          {receipt.currentTaskActionError === null ? null : <Flash variant="warning">Current task action still needs attention: {receipt.currentTaskActionError}</Flash>}
        </section>
      )}

      {proposal.status === 'proposed' ? (
        <footer className="team-change-actions">
          <label>Decision note<input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional audit context" /></label>
          <Button variant="danger" disabled={pending} onClick={() => onReject(note)}>Reject</Button>
          <Button variant="primary" disabled={pending} onClick={() => onApply(note)}>{pending ? 'Applying…' : 'Apply to team'}</Button>
        </footer>
      ) : (
        <p className="proposal-decision">Decided by {proposal.decisionActorId ?? 'unknown'}{proposal.decisionNote ? ` — ${proposal.decisionNote}` : ''}</p>
      )}
      {error === null ? null : <Flash variant="danger" role="alert">{error.message}</Flash>}
    </article>
  );
}

/** Connects the review card to the existing human Proposal decision endpoints. */
export function TeamChangeProposalReview({ proposal }: { readonly proposal: Proposal }): ReactNode {
  const decide = useConsoleMutation<{ readonly decision: 'approve' | 'reject'; readonly note: string }, Proposal>(
    (data, context, input) => input.decision === 'approve'
      ? data.approveProposal(proposal.id, proposal.revision, input.note, context)
      : data.rejectProposal(proposal.id, proposal.revision, input.note, context),
    [['proposals'], ['board'], ['human-reviews'], ['team-status'], ['skills'], ['team-versions']],
  );
  return <TeamChangeProposalCard
    proposal={proposal}
    pending={decide.isPending}
    error={decide.error}
    onApply={(note) => decide.mutate({ decision: 'approve', note })}
    onReject={(note) => decide.mutate({ decision: 'reject', note })}
  />;
}
